import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import { useIsMobile } from './hooks/useIsMobile';
import Header from './components/Layout/Header';
import Navigation from './components/Layout/Navigation';
import LoginPage from './components/Auth/LoginPage';
import OrdersPage from './components/Orders/OrdersPage';
import LogisticsPage from './components/Logistics/LogisticsPage';
import WarehousePage, { CellcomOrdersPage } from './components/Warehouse/WarehousePage';
import FCOrdersPage from './components/FCOrders/FCOrdersPage';
import PricesPage from './components/Prices/PricesPage';
import ProfitabilityPage from './components/Profitability/ProfitabilityPage';
import PortalPage from './components/Portal/PortalPage';
import SettingsPage from './components/Settings/SettingsPage';
import SerialNumbersPage from './components/SerialNumbers/SerialNumbersPage';
import DashboardPage from './components/Dashboard/DashboardPage';
import WarehouseDashboardPage from './components/WarehouseDashboard/WarehouseDashboardPage';
import DataForm from './components/Public/DataForm';
import UploadCenterPage from './components/Uploads/UploadCenterPage';
import DeliveryNotesPage from './components/DeliveryNotes/DeliveryNotesPage';
import CustomerPaymentsPage from './components/CustomerPayments/CustomerPaymentsPage';

function RoleRoute({ children, roles, desktopOnly }) {
  const { hasRole, user } = useAuth();
  const isMobile = useIsMobile();
  if (!hasRole(...roles)) return <Navigate to="/" replace />;
  if (desktopOnly && isMobile) {
    return <Navigate to={user?.role === 'warehouse' ? '/warehouse-dashboard' : '/dashboard'} replace />;
  }
  return children;
}

function DefaultRedirect() {
  const { user } = useAuth();
  if (user?.role === 'customer') return <Navigate to="/portal" replace />;
  if (user?.role === 'warehouse') return <Navigate to="/warehouse-dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  const { user, loading } = useAuth();
  const { i18n } = useTranslation();

  useEffect(() => {
    document.dir = i18n.language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  if (loading) {
    return <div className="loading"><div className="spinner" /> Loading...</div>;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/form/:token" element={<DataForm />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Customer-only layout: portal page only, no navigation
  if (user?.role === 'customer') {
    return (
      <div className="app-layout">
        <Header />
        <div className="page-content">
          <Routes>
            <Route path="/portal" element={<PortalPage />} />
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Header />
      <Navigation />
      <div className="page-content">
        <Routes>
          <Route path="/form/:token" element={<DataForm />} />
          <Route path="/" element={<DefaultRedirect />} />
          <Route path="/dashboard" element={<RoleRoute roles={['admin','accounting','brand_manager','viewer','account_manager']}><DashboardPage /></RoleRoute>} />
          <Route path="/warehouse-dashboard" element={<RoleRoute roles={['admin','warehouse']}><WarehouseDashboardPage /></RoleRoute>} />
          <Route path="/orders" element={<RoleRoute roles={['admin','accounting','brand_manager','viewer','account_manager']} desktopOnly><OrdersPage /></RoleRoute>} />
          <Route path="/logistics" element={<RoleRoute roles={['admin','brand_manager','warehouse','viewer','account_manager']} desktopOnly><LogisticsPage /></RoleRoute>} />
          <Route path="/warehouse" element={<RoleRoute roles={['admin','warehouse','viewer','account_manager']} desktopOnly><WarehousePage /></RoleRoute>} />
          <Route path="/cellcom-orders" element={<RoleRoute roles={['admin','warehouse','viewer','account_manager']} desktopOnly><CellcomOrdersPage /></RoleRoute>} />
          <Route path="/fc-orders" element={<RoleRoute roles={['admin','viewer']} desktopOnly><FCOrdersPage /></RoleRoute>} />
          <Route path="/prices" element={<RoleRoute roles={['admin','accounting','brand_manager','viewer','account_manager']} desktopOnly><PricesPage /></RoleRoute>} />
          <Route path="/profitability" element={<RoleRoute roles={['admin','accounting','viewer','account_manager']} desktopOnly><ProfitabilityPage /></RoleRoute>} />
          <Route path="/portal" element={<RoleRoute roles={['admin','accounting','brand_manager','viewer','account_manager','customer']} desktopOnly><PortalPage /></RoleRoute>} />
          <Route path="/uploads" element={<RoleRoute roles={['admin','account_manager']} desktopOnly><UploadCenterPage /></RoleRoute>} />
          <Route path="/serial-numbers" element={<RoleRoute roles={['admin','warehouse']}><SerialNumbersPage /></RoleRoute>} />
          <Route path="/delivery-notes" element={<RoleRoute roles={['admin','warehouse','viewer','account_manager']} desktopOnly><DeliveryNotesPage /></RoleRoute>} />
          <Route path="/customer-payments" element={<RoleRoute roles={['admin']} desktopOnly><CustomerPaymentsPage /></RoleRoute>} />
          <Route path="/settings" element={<RoleRoute roles={['admin']} desktopOnly><SettingsPage /></RoleRoute>} />
          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
      </div>
    </div>
  );
}
// cache-bust
